/**
 * Notification Routing Service
 * 
 * Determines which users should receive notifications based on the plan's requirements:
 * - Sales reps only receive notifications for assigned stores or stores they've submitted notes to
 * - All users receive resource library notifications
 * - Managers receive all store notifications
 */

import { NotificationServiceV2 } from './NotificationServiceV2';
import { StoreService } from './StoreService';
import { userService } from './UserService';
import { Store } from '../@types/store';
import { User } from '../contexts/AuthContext';

export class NotificationRoutingService {
  /**
   * Get users to notify when a note is added to a store
   * Rules:
   * - Sales reps: Only if they are assigned to the store OR have previously submitted notes to it
   * - Managers: All managers receive notifications for all stores
   */
  static async getUsersToNotifyForNote(
    storeId: string,
    noteCreatedByUserId: string
  ): Promise<string[]> {
    try {
      const userIdsToNotify = new Set<string>();

      // Get store data
      const store = await StoreService.getStoreById(storeId);
      if (!store) {
        console.warn(`Store ${storeId} not found`);
        return [];
      }

      // Get all users
      const allUsers = await userService.getAllUsers();

      // Get users who have submitted notes to this store
      const usersWithNoteHistory = await NotificationServiceV2.getUsersWithNoteHistory(storeId);

      for (const user of allUsers) {
        // Skip the user who created the note
        if (user.id === noteCreatedByUserId) {
          continue;
        }

        // Managers receive all notifications
        if (user.role === 'manager') {
          userIdsToNotify.add(user.id);
          continue;
        }

        // Sales reps: Check if assigned or have note history
        if (user.role === 'sales-representative') {
          // Check if assigned via salespersonNo
          if (store.salespersonNo === user.id) {
            userIdsToNotify.add(user.id);
            continue;
          }

          // Check if assigned via assignedToUserIds
          if (store.assignedToUserIds && store.assignedToUserIds.includes(user.id)) {
            userIdsToNotify.add(user.id);
            continue;
          }

          // Check if user has previously submitted notes to this store
          if (usersWithNoteHistory.includes(user.id)) {
            userIdsToNotify.add(user.id);
            continue;
          }
        }
      }

      return Array.from(userIdsToNotify);
    } catch (error: any) {
      console.error('Error getting users to notify for note:', error);
      return [];
    }
  }

  /**
   * Get users to notify when an order is submitted
   * Rules:
   * - Managers: All managers receive notifications for all orders
   * - Sales reps: Do not receive order submission notifications
   */
  static async getUsersToNotifyForOrder(storeId: string): Promise<string[]> {
    try {
      const userIdsToNotify: string[] = [];

      // Get all users
      const allUsers = await userService.getAllUsers();

      // Only managers receive order submission notifications
      for (const user of allUsers) {
        if (user.role === 'manager') {
          userIdsToNotify.push(user.id);
        }
      }

      return userIdsToNotify;
    } catch (error: any) {
      console.error('Error getting users to notify for order:', error);
      return [];
    }
  }

  /**
   * Get users to notify when a resource is added
   * Rules:
   * - All users (sales reps and managers) receive resource library notifications
   */
  static async getUsersToNotifyForResource(): Promise<string[]> {
    try {
      const allUsers = await userService.getAllUsers();
      return allUsers.map(user => user.id);
    } catch (error: any) {
      console.error('Error getting users to notify for resource:', error);
      return [];
    }
  }
}

